import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, FileText, FolderUp, LogOut, Plus } from "lucide-react";
import Logo from "@/components/Logo";

const items = [
  { to: "/app", label: "Overview", icon: LayoutGrid, end: true },
  { to: "/app/reports", label: "Reports", icon: FileText },
  { to: "/app/documents", label: "Documents", icon: FolderUp },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const signOut = () => {
    logout();
    nav("/");
  };

  return (
    <div className="min-h-screen paper text-[var(--ink)] flex" data-testid="dashboard-layout">
      <aside className="w-64 shrink-0 bg-[var(--cream)] border-r border-[var(--ink)]/15 flex flex-col sticky top-0 h-screen">
        <Link to="/app" className="px-6 py-6 border-b border-[var(--ink)]/15 block" data-testid="sidebar-logo">
          <Logo size={20} />
          <div className="mt-2 text-[9px] tracking-[0.22em] uppercase font-semibold text-[var(--warm-grey)]">workspace</div>
        </Link>
        <div className="p-4">
          <Link
            to="/app/reports/new"
            className="w-full inline-flex items-center justify-center gap-2 bg-[var(--ink)] hover:bg-[var(--ink)]/90 text-[var(--cream)] py-3 text-[13px] tracking-wide transition-colors duration-200"
            data-testid="sidebar-new-report"
          >
            <Plus className="w-3.5 h-3.5" /> New report
          </Link>
        </div>
        <nav className="flex-1 px-2 space-y-0.5">
          {items.map((i) => {
            const Icon = i.icon;
            return (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 text-[13px] transition-colors duration-200 ${
                    isActive ? "bg-[var(--ink)]/8 text-[var(--ink)] font-medium border-l-2 border-[var(--saffron)]" : "text-[var(--ink)]/70 hover:text-[var(--ink)] hover:bg-[var(--ink)]/4 border-l-2 border-transparent"
                  }`
                }
                data-testid={`sidebar-link-${i.label.toLowerCase()}`}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                {i.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-[var(--ink)]/15 p-5">
          <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--warm-grey)]" data-testid="sidebar-user-email">{user?.email}</div>
          <div className="font-serif text-[15px] mt-1 truncate" data-testid="sidebar-user-name">{user?.full_name}</div>
          <button
            onClick={signOut}
            className="mt-4 w-full flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-semibold text-[var(--warm-grey)] hover:text-[var(--ink)] transition-colors"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
